import { Edge, Node } from "@xyflow/react";
import { PERSISTED_NODES } from "@zapier/canvas-constants";
import {
  Canvas,
  IncomingBackendEdge,
  IncomingBackendNode,
  NodeData,
  PersistedNodeType,
  RawCanvas,
  TextNodeData,
} from "@zapier/canvas-state";
import { MARKER } from "@zapier/canvas-state/src/constants";
import type { RemirrorJSON } from "@zapier/rich-text-editor";
import { removeNullOrUndefined } from "./removeNullOrUndefined";

type Params = {
  rawCanvas: RawCanvas;
  isViewOnlyMode: boolean;
};

function isAllowedNodeType(node: Node<NodeData>): boolean {
  return PERSISTED_NODES.includes(node.type as PersistedNodeType);
}

/**
 * Attempts to parse a JSON string. Returns the parsed object if successful, otherwise returns the original string.
 * @param text The string to parse.
 * @returns The parsed object or the original string.
 */
function tryParseJSON(text: string): string | RemirrorJSON {
  try {
    return JSON.parse(text as string);
  } catch {
    return text as string;
  }
}

/**
 * Converts a backendNode to a ReactFlow Node with appropriate type data.
 * Removes any undefined values from the node data before assigning.
 */
function convertBackendNodeToReactFlowNode(
  backendNode: IncomingBackendNode,
): Node<NodeData> {
  // Parse label if it's a JSON string (for text node)
  const stringOrJson = tryParseJSON(backendNode.label);

  const dataWithUndefinedValues = {
    label: stringOrJson,
    zapId: backendNode.zap_id,
    tableId: backendNode.table_id,
    chatbotId: backendNode.chatbot_id,
    projectId: backendNode.project_id,
    pageId: backendNode.page_id,
    isExpanded: backendNode.is_expanded,
    notesJson: backendNode.notes, // JSON object or undefined
    color: backendNode.color,
    backgroundColor: backendNode.background_color,
    app: backendNode.app,
    services: backendNode.services,
    actors: backendNode.actors,
    timeSpent: backendNode.time_spent,
  };

  // Remove undefined values from the raw data
  const data = removeNullOrUndefined(dataWithUndefinedValues) as NodeData;

  return {
    id: backendNode.id,
    type: backendNode.node_type,
    position: {
      x: backendNode.x,
      y: backendNode.y,
    },
    data,
    measured: {
      width: backendNode.width,
      height: backendNode.height,
    },
    ...(backendNode.parent_id && { parentId: backendNode.parent_id }),
  };
}

/**
 * Applies node type-specific adjustments to a FE node.
 */
export function applyFeNodeAdjustmentsByType<T extends Node<NodeData>>(
  feNode: T,
  isViewOnlyMode: boolean,
): T {
  const baseProps = {
    ...feNode,
    deletable: !isViewOnlyMode,
    ...(feNode.parentId && {
      expandParent: true,
      extent: "parent" as const,
    }),
  };

  /*
    In view-only mode, path nodes are never selectable regardless of the context 'cuz we can see everything inline already,
    so don't allow opening the sidebar (or else we need to disable a bunch of stuff)
  */
  if (feNode.type === "path") {
    return {
      ...baseProps,
      selectable: !isViewOnlyMode,
    } as T;
  }

  if (feNode.type === "group") {
    return {
      ...baseProps,
      selectable: !isViewOnlyMode,
      connectable: false,
      width: feNode.measured?.width ?? 200, // need this or else group nodes won't draw correctly on page refresh
      height: feNode.measured?.height ?? 200, // need this or else group nodes won't draw correctly on page refresh
    } as T;
  }

  if (feNode.type === "text") {
    return {
      ...baseProps,
      connectable: false,
      width: feNode.measured?.width, // need this or else text nodes won't draw correctly on page refresh
      height: feNode.measured?.height, // need this or else text nodes won't draw correctly on page refresh
      style: {
        backgroundColor: (feNode.data as TextNodeData).backgroundColor,
      },
    } as T;
  }

  /*
    In view-only mode, we allow nodes to be selectable so user can see the notes in the sidebar.
    Only need this for viewer access and public link, not templates, embeds or view-only mode.
    We set elementsSelectable globally as false on <ReactFlow> for templates, embeds and view-only mode.

    Don't hard code selectable since it works naturally without it set. Let elementsSelectable prop on <ReactFlow> take precedence.
  */
  return {
    ...baseProps,
    connectable: !isViewOnlyMode,
  } as T;
}

export function getEdgeSourceId(backendEndEdge: IncomingBackendEdge): string {
  if (
    backendEndEdge.source_zap_step_id &&
    backendEndEdge.source_zap_step_id.startsWith("zap")
  ) {
    // legacy support due to saving wrong format to the BE
    // TODO: Can be deprecated in summer 2025
    return backendEndEdge.source_zap_step_id;
  }

  if (backendEndEdge.source_zap_step_id) {
    return `zap-${backendEndEdge.source_id}-${backendEndEdge.source_zap_step_id}`;
  }

  return backendEndEdge.source_id;
}

export function getEdgeTargetId(backendEndEdge: IncomingBackendEdge): string {
  if (
    backendEndEdge.target_zap_step_id &&
    backendEndEdge.target_zap_step_id.startsWith("zap")
  ) {
    // legacy support due to saving wrong format to the BE
    // TODO: Can be deprecated in summer 2025
    return backendEndEdge.target_zap_step_id;
  }

  if (backendEndEdge.target_zap_step_id) {
    return `zap-${backendEndEdge.target_id}-${backendEndEdge.target_zap_step_id}`;
  }

  return backendEndEdge.target_id;
}

/**
 * Converts a backend edge to a ReactFlow edge with appropriate props.
 * Reverse of convertReactFlowEdgeToBackendEdge
 */
function convertBackendEdgeToReactFlowEdge(
  backendEndEdge: IncomingBackendEdge,
): Edge {
  // Ensure required properties are always present
  return {
    id: backendEndEdge.id,
    source: getEdgeSourceId(backendEndEdge),
    target: getEdgeTargetId(backendEndEdge),
    // Optional properties can be removed if undefined
    ...removeNullOrUndefined({
      sourceHandle: backendEndEdge.source_handle,
      targetHandle: backendEndEdge.target_handle,
      label: backendEndEdge.label,
    }),
  };
}

export function applyEdgeAdjustmentsByMode(
  edge: Edge,
  isViewOnlyMode: boolean,
): Edge {
  return {
    ...edge,
    deletable: !isViewOnlyMode,
    selectable: !isViewOnlyMode,
  };
}

const ALLOWED_HANDLE_IDS = ["top", "right", "bottom", "left"];

function getSourceHandleId(edge: Edge) {
  if (edge.sourceHandle) {
    const hasRight = edge.sourceHandle.includes("right");
    if (hasRight) {
      return "right";
    }

    const hasBottom = edge.sourceHandle.includes("bottom");
    if (hasBottom) {
      return "bottom";
    }

    return ALLOWED_HANDLE_IDS.includes(edge.sourceHandle)
      ? edge.sourceHandle
      : "right";
  }

  return "right";
}

function getTargetHandleId(edge: Edge) {
  if (edge.targetHandle) {
    const hasLeft = edge.targetHandle.includes("left");
    if (hasLeft) {
      return "left";
    }

    const hasTop = edge.targetHandle.includes("top");
    if (hasTop) {
      return "top";
    }

    return ALLOWED_HANDLE_IDS.includes(edge.targetHandle)
      ? edge.targetHandle
      : "left";
  }

  return "left";
}

// *** This is what we use to convert a backend node to a RF node completely ***
export function fullyConvertToReactFlowNode(
  node: IncomingBackendNode,
  isViewOnlyMode: boolean,
) {
  const feNode = convertBackendNodeToReactFlowNode(node);
  return applyFeNodeAdjustmentsByType(feNode, isViewOnlyMode);
}

// *** This is what we use to convert a backend edge to a RF edge completely ***
export function fullyConvertToReactFlowEdge(
  edge: IncomingBackendEdge,
  isViewOnlyMode: boolean,
) {
  const feEdge = convertBackendEdgeToReactFlowEdge(edge);

  return {
    ...applyEdgeAdjustmentsByMode(feEdge, isViewOnlyMode),
    sourceHandle: getSourceHandleId(feEdge),
    targetHandle: getTargetHandleId(feEdge),
    markerEnd: MARKER,
  };
}

/**
 * Converts the entire Canvas schema for use with React Flow,
 * ensuring nodes are valid, free from null values, and correctly structured.
 *
 * Hydrates props removed by processNodesBeforeSave function
 */
export function convertCanvasSchemaForReactFlow({
  rawCanvas,
  isViewOnlyMode,
}: Params): Canvas {
  const processedNodes: Node<NodeData>[] = rawCanvas.nodes.reduce<
    Node<NodeData>[]
  >((acc, node) => {
    const reactFlowNode = fullyConvertToReactFlowNode(node, isViewOnlyMode);

    if (isAllowedNodeType(reactFlowNode)) {
      acc.push(reactFlowNode);
    }

    return acc;
  }, []);

  // Collect all valid node IDs
  const nodeIds = new Set<string>(processedNodes.map((node) => node.id));

  // **Pass 2:** Ensure that a node does not reference a non-existing parentId which would cause RF to crash
  const validatedNodes = processedNodes.map((node) => {
    if (node.parentId && !nodeIds.has(node.parentId)) {
      // Remove parentId and related properties if parent does not exist
      const { parentId, expandParent, extent, ...rest } = node;
      return rest;
    }
    return node;
  });

  const convertedEdges = rawCanvas.edges.map((edge) =>
    fullyConvertToReactFlowEdge(edge, isViewOnlyMode),
  );

  return {
    ...rawCanvas,
    nodes: validatedNodes,
    edges: convertedEdges,
  };
}
